import { lazy, type ComponentType } from 'react';

// Handle common chunk loading failures (stale deployments, CDN cache, etc.)
const errorPattern =
  /Failed to fetch dynamically imported module|ChunkLoadError|Loading chunk \d+ failed|Importing a module script failed/i;

/**
 * Wrap `React.lazy` with automatic retry + cache-bust logic.
 *
 * 1. First failure → retry the import with a `?t=<timestamp>` cache-bust query.
 * 2. Second failure → force a hard page reload (once per session per module).
 * 3. Third failure → propagate the error to the ErrorBoundary.
 */
export function lazyWithRetry<T extends ComponentType<any>>(
  importer: () => Promise<{ default: T }>,
  moduleName?: string,
) {
  return lazy(async () => {
    // --- Attempt 1: normal import ---
    try {
      return await importer();
    } catch (firstError) {
      const msg1 = firstError instanceof Error ? firstError.message : String(firstError);
      if (!errorPattern.test(msg1)) throw firstError;

      // --- Attempt 2: cache-busted re-import ---
      // Extract the module URL from the error message and retry with a fresh query string
      // so the browser fetches the latest version from the server.
      try {
        const urlMatch = msg1.match(/https?:\/\/[^\s'"]+/);
        if (urlMatch) {
          const freshUrl = `${urlMatch[0].split('?')[0]}?t=${Date.now()}`;
          const mod = await import(/* @vite-ignore */ freshUrl);
          return mod as { default: T };
        }
      } catch {
        // cache-bust import failed — fall through to page reload
      }

      // --- Attempt 3: full page reload (once per module per session) ---
      if (typeof window !== 'undefined') {
        try {
          const key = `lazy-retry:${moduleName ?? 'global'}`;
          const retried = window.sessionStorage.getItem(key);
          if (!retried) {
            window.sessionStorage.setItem(key, 'true');
            window.location.reload();
            // Return a never-resolving promise so the old render tree stays
            // visible until the browser navigates away.
            return new Promise<never>(() => {});
          }
        } catch {
          window.location.reload();
          return new Promise<never>(() => {});
        }
      }

      throw firstError;
    }
  });
}
